import { composeApiURL } from "helpers/utility/axiosHelpers";
import axios from "helpers/axiosInstance";
import abortControllerManager from "helpers/abortControllerManager";
import { fetchFolderMetadata } from "redux/CreateDataset/actions";
import { postRequest } from "../utility/axiosServiceLayer";
import { SetFoldersResponse } from "../../types";

const FOLDER_STRUCTURE = {
  REQUIRED_FILES: {
    DB_CSV: "db.csv",
    CLASSES_JSON: "classes.json",
    METADATA_JSON: "metadata.json",
  },
  IMAGES_FOLDER: "images",
};

// System files to ignore during validation (macOS system files only)
const IGNORE_FILES = [
  ".DS_Store",
  "__MACOSX",
  ".AppleDouble",
  ".Trashes",
  ".Spotlight-V100",
  ".fseventsd",
];

export interface FolderValidationResult {
  isValid: boolean;
  errors: string[];
  detectedFiles: {
    metaData: string | null;
    classes: string | null;
    dbCsv: string | null;
    imagesFolder: boolean;
  };
}

export interface FolderGroup {
  folderName: string;
  files: File[];
}

/**
 * Checks if a file should be ignored during validation
 * Checks the entire path, not just the filename
 */
function shouldIgnoreFile(relativePath: string): boolean {
  const normalizedPath = relativePath.toLowerCase();
  return IGNORE_FILES.some((ignoreFile) =>
    normalizedPath.includes(ignoreFile.toLowerCase()),
  );
}

/**
 * Validates a folder structure according to the required format:
 * - 2 JSON files: DB_export_${id}.json and Classes.json
 * - 1 CSV file: DB.csv
 * - 1 images directory
 */
export function validateFolderStructure(
  folderGroup: FolderGroup,
): FolderValidationResult {
  const { folderName, files } = folderGroup;
  const errors: string[] = [];

  // Track detected files
  const detectedFiles = {
    metaData: null as string | null,
    classes: null as string | null,
    dbCsv: null as string | null,
    imagesFolder: false,
  };

  // Check if we have files
  if (files.length === 0) {
    errors.push("No files found in folder");
    return { isValid: false, errors, detectedFiles };
  }
  console.log("files", files);

  // Analyze file structure
  for (const file of files) {
    const relativePath = file.webkitRelativePath || file.name;
    const pathParts = relativePath.split("/");

    // Skip the root folder name
    if (pathParts[0] === folderName) {
      pathParts.shift();
    }

    if (pathParts.length === 0) continue;

    const fileName = pathParts[0].toLowerCase();

    // Skip system files that should be ignored
    if (shouldIgnoreFile(relativePath)) {
      continue;
    }

    // Check for required files
    if (fileName === FOLDER_STRUCTURE.REQUIRED_FILES.DB_CSV) {
      detectedFiles.dbCsv = fileName;
    } else if (fileName === FOLDER_STRUCTURE.REQUIRED_FILES.CLASSES_JSON) {
      detectedFiles.classes = fileName;
    } else if (fileName === FOLDER_STRUCTURE.REQUIRED_FILES.METADATA_JSON) {
      detectedFiles.metaData = fileName;
    } else if (fileName === FOLDER_STRUCTURE.IMAGES_FOLDER) {
      // Check if it's actually a directory (has subfiles)
      const hasSubFiles = files.some((f) => {
        const fPath = f.webkitRelativePath || f.name;
        const fParts = fPath.split("/");
        return (
          fParts[0] === folderName &&
          fParts[1] === FOLDER_STRUCTURE.IMAGES_FOLDER &&
          fParts.length > 2
        );
      });

      if (hasSubFiles) {
        detectedFiles.imagesFolder = true;
      } else {
        errors.push(
          "Images folder must contain subdirectories, not files directly",
        );
      }
    }
  }

  // Validate required files are present
  if (!detectedFiles.metaData) {
    errors.push(
      `Missing required file: ${FOLDER_STRUCTURE.REQUIRED_FILES.METADATA_JSON}`,
    );
  }

  if (!detectedFiles.classes) {
    errors.push(
      `Missing required file: ${FOLDER_STRUCTURE.REQUIRED_FILES.CLASSES_JSON}`,
    );
  }

  if (!detectedFiles.dbCsv) {
    errors.push(
      `Missing required file: ${FOLDER_STRUCTURE.REQUIRED_FILES.DB_CSV}`,
    );
  }

  if (!detectedFiles.imagesFolder) {
    errors.push(
      `Missing required ${FOLDER_STRUCTURE.IMAGES_FOLDER} folder with image files`,
    );
  }
  const detectedRootItems = new Set<string>();

  for (const file of files) {
    const relativePath = file.webkitRelativePath || file.name;
    const pathParts = relativePath.split("/");

    if (pathParts[0] === folderName && pathParts.length > 1) {
      const rootItem = pathParts[1].toLowerCase();

      // Skip system files that should be ignored
      if (shouldIgnoreFile(relativePath)) {
        continue;
      }

      if (rootItem === FOLDER_STRUCTURE.REQUIRED_FILES.METADATA_JSON) {
        detectedRootItems.add(FOLDER_STRUCTURE.REQUIRED_FILES.METADATA_JSON);
      } else {
        detectedRootItems.add(rootItem);
      }
    }
  }

  // const extraItems = Array.from(detectedRootItems).filter(
  //   (item) =>
  //     !allowedRootItems.includes(item) &&
  //     item !== FOLDER_STRUCTURE.REQUIRED_FILES.METADATA_JSON,
  // );

  // if (extraItems.length > 0) {
  //   errors.push(
  //     `Extra files/folders found. Only ${FOLDER_STRUCTURE.REQUIRED_FILES.DB_CSV}, ${FOLDER_STRUCTURE.REQUIRED_FILES.METADATA_JSON}, ${FOLDER_STRUCTURE.REQUIRED_FILES.CLASSES_JSON}, and an ${FOLDER_STRUCTURE.IMAGES_FOLDER} folder are allowed.`,
  //   );
  // }

  const isValid = errors.length === 0;

  return { isValid, errors, detectedFiles };
}

/**
 * Extracts relative path without root folder name
 */
function getRelativePathWithoutRoot(
  relativePath: string,
  folderName: string,
): string {
  const pathParts = relativePath.split("/");
  if (pathParts[0] === folderName && pathParts.length > 1) {
    return pathParts.slice(1).join("/");
  }
  return relativePath;
}

/**
 * Frontend validation: Categorizes files and prepares validation data
 */

type ValidateFE = { formData: FormData; paths: string[] };

function validateFE(folder: FolderGroup): ValidateFE {
  const formData = new FormData();
  const paths: string[] = [];

  // Categorize files and add to FormData
  folder.files.forEach((file) => {
    const relativePathNoRoot = getRelativePathWithoutRoot(
      file.webkitRelativePath,
      folder.folderName,
    );
    const fileName = relativePathNoRoot.split("/").pop()?.toLowerCase() || "";

    // Check for exact file names that backend expects
    if (fileName === "db.csv") {
      formData.append("db", file);
    } else if (fileName === "classes.json") {
      formData.append("classes", file);
    } else if (fileName === FOLDER_STRUCTURE.REQUIRED_FILES.METADATA_JSON) {
      formData.append("metadata", file);
    }

    paths.push(relativePathNoRoot);
  });

  return { formData, paths };
}

/**
 * Validates a single folder against backend requirements
 */
async function validateFolderWithBackend(
  folder: FolderGroup,
  newDatasetId: string,
  batchId?: string,
): Promise<{ isValid: boolean; error: string | null }> {
  if (!folder.folderName || folder.folderName.trim() === "") {
    return { isValid: false, error: "Folder name cannot be empty" };
  }

  try {
    // frontend validation
    const { formData, paths } = validateFE(folder);

    formData.append("paths_payload", JSON.stringify({ paths: paths }));

    if (batchId) {
      formData.append("batch_id", batchId);
    }

    // Call backend validation API - folder_name goes as query parameter
    const url = composeApiURL(
      process.env.REACT_APP_API_ENDPOINT!,
      `/dataset/${newDatasetId}/data/pre_upload_validation?folder_name=${encodeURIComponent(folder.folderName)}`,
    );

    const abortController = abortControllerManager.getController();
    const response = await axios.post(url, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      signal: abortController.signal,
    });
    const backendResponse = response.data;

    // Check backend validation response
    if (backendResponse?.success) {
      return { isValid: true, error: null };
    } else {
      return { isValid: false, error: backendResponse.detail };
    }
  } catch (error: any) {
    // Don't log errors if request was aborted
    if (error.name === "AbortError" || error.code === "ERR_CANCELED") {
      return { isValid: false, error: "Validation cancelled" };
    }

    let errorMessage = "Unknown backend validation error";

    if (error?.response?.data?.detail) {
      errorMessage = error.response.data.detail;
    }
    return { isValid: false, error: errorMessage };
  }
}
/**
 * Validates multiple folders and returns results for each
 */
export async function validateMultipleFolders(
  folderGroups: FolderGroup[],
  newDatasetId: string,
  dispatch: any,
  batchId?: string,
): Promise<{
  validFolders: FolderGroup[];
  invalidFolders: Array<
    FolderGroup & { validationResult: FolderValidationResult }
  >;
}> {
  // const validFolders: FolderGroup[] = [];
  // const invalidFolders: Array<
  //   FolderGroup & { validationResult: FolderValidationResult }
  // > = [];

  // Commented out FE validations for now
  // for (const folderGroup of folderGroups) {
  //   const validationResult = validateFolderStructure(folderGroup);

  //   //FE Validations
  //   if (validationResult.isValid) {
  //     validFolders.push(folderGroup);
  //   } else {
  //     invalidFolders.push({
  //       ...folderGroup,
  //       validationResult,
  //     });
  //   }
  // }

  // If no folders passed frontend validation, return early
  // if (validFolders.length === 0) {
  //   return { validFolders, invalidFolders };
  // }
  dispatch(fetchFolderMetadata(newDatasetId!) as any);

  // Skip frontend validation and proceed directly to backend validation for all folders
  const backendValidationPromises = folderGroups.map(async (folder) => {
    const result = await validateFolderWithBackend(
      folder,
      newDatasetId,
      batchId,
    );
    return { folder, ...result };
  });

  // Wait for all backend validations to complete
  const backendResults = await Promise.all(backendValidationPromises);

  // Ensure metadata is up to date after validation
  await dispatch(fetchFolderMetadata(newDatasetId) as any);

  // Process backend validation results
  const finalValidFolders: FolderGroup[] = [];
  const finalInvalidFolders: Array<
    FolderGroup & { validationResult: FolderValidationResult }
  > = []; // Start with empty since we're skipping frontend validation

  backendResults.forEach((result) => {
    if (result.isValid) {
      finalValidFolders.push(result.folder);
    } else {
      // Create validation result for backend validation failure
      const validationResult: FolderValidationResult = {
        isValid: false,
        errors: [result.error || "Backend validation failed"],
        detectedFiles: {
          metaData: null,
          classes: null,
          dbCsv: null,
          imagesFolder: false,
        },
      };

      finalInvalidFolders.push({ ...result.folder, validationResult });
    }
  });

  return {
    validFolders: finalValidFolders,
    invalidFolders: finalInvalidFolders,
  };
}

export const validateFoldersPathsWithBackend = async (
  newDatasetId: string,
  paths: string[],
  batchId?: string,
) => {
  const url = composeApiURL(
    process.env.REACT_APP_API_ENDPOINT!,
    `/workflow/dataset/${newDatasetId}/set_folders`,
  );
  return postRequest<SetFoldersResponse, any>(url, {
    payload: {
      folder_paths: paths,
      ...(batchId && { batch_id: batchId }),
    },
  });
};
